import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CheckCheck, ChevronRight, MoreVertical } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { NextGenEntityModelPanel } from 'src/features/messages/components/feeds/reading-pane/next-gen';
import { ActualsMetaNer, ActualsUpdateType, Entities, MessageType } from 'src/features/messages/types/message.types';
import EmptyStateCard from 'src/shared/components/ui/cards/empty-state-card';
import { useModels } from 'src/features/models/queries/useModels';
import { getModelMap } from 'src/shared/lib/model.utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import JsonViewer from 'src/shared/components/typography/JsonViewer';
import { getEntityValue, setEntityValue } from 'src/shared/lib/entities.utils';
import SubtleCapsHeaders from 'src/shared/components/typography/SubtleCapsHeaders';
import { useMutateMessage } from 'src/features/messages/queries/useMutateMessage';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import useThrottledLoading from 'src/shared/hooks/useThrottledLoading';
import { Loading } from 'src/shared/components/ui/loaders/loading';
import EntityTreeErrorFallback from 'src/shared/components/entities/EntityTreeErrorFallback';
import { cn } from 'src/shared/lib/utils';
import { parseLocalStorage } from 'src/shared/lib/localStorage.utils';
import SuperSmartEntityTree from 'src/shared/components/entities/SuperSmartEntityTree';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import { Tag } from 'src/shared/types/model.types';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import mixpanel from 'src/shared/lib/mixpanel';
import { useFeatureFlag } from 'src/shared/queries/utils/useFeatureFlag';
import { useExtractionModels } from 'src/features/models/queries/next-gen/useExtractionModels';
import { isDeepEqual } from 'src/shared/lib/comparison.utils';
import TripleDotActions from 'src/shared/components/ui/dropdowns/triple-dot-actions';
import { useToast } from 'src/shared/components/ui/use-toast';

const SMALL_AREA_VERSION_THRESHOLD = 500;

type EntitiesTabProps = {
  message: MessageType;
  onEntityChange?: (action: ActualsUpdateType) => void;
};

type EntityViewAs = 'TREE' | 'RAW';

export default function EntitiesTab({ message, onEntityChange }: EntitiesTabProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: models } = useModels();
  const { data: hasNextGenModelsFlag } = useFeatureFlag('NEXT_GEN_MODELS');
  const { data: nextGenModels } = useExtractionModels();
  const { updateMessage } = useMutateMessage();
  const [error, setError] = useState<string[]>([]);
  const [accordionOpen, setAccordionOpen] = useState<string[]>([]);
  const [entities, setEntities] = useState<Entities>(message.entities);
  const [viewAs] = useState<EntityViewAs>(parseLocalStorage('entity-view-as', 'TREE'));
  const [smallVersion, setSmallVersion] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const throttledLoading = useThrottledLoading(updateMessage.isPending);

  const modelMap = useMemo(() => {
    return getModelMap(models || []);
  }, [models]);

  // Old entity models linked to this inbox
  const linkedEntityModels = models?.filter(
    (model) => model.isNer && (model.inboxes.all || model.inboxes.ids.includes(message.integration_id))
  );

  // Next-gen extraction models (when feature flag is enabled)
  // Note: ExtractionModelListItem doesn't include integrations array, so we show all active models
  // The backend should handle which models' entities are present on the message
  const linkedNextGenModels = useMemo(() => {
    if (!hasNextGenModelsFlag || !nextGenModels) return [];
    return nextGenModels.filter((model) => model.status === 'ACTIVE');
  }, [hasNextGenModelsFlag, nextGenModels]);

  useLayoutEffect(() => {
    const checkAccordionWidth = () => {
      if (accordionRef.current) {
        const width = accordionRef.current.getBoundingClientRect().width;
        setSmallVersion(width < SMALL_AREA_VERSION_THRESHOLD);
      }
    };

    checkAccordionWidth();

    const resizeObserver = new ResizeObserver(checkAccordionWidth);
    if (accordionRef.current) {
      resizeObserver.observe(accordionRef.current);
    }

    window.addEventListener('resize', checkAccordionWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkAccordionWidth);
    };
  }, []);

  // Helper function to check if an entity model has any non-null values
  const hasValues = (entityModel: Entities[string]): boolean => {
    if (!entityModel || typeof entityModel !== 'object') return false;

    for (const value of Object.values(entityModel)) {
      if (value === null || value === undefined) continue;

      if (Array.isArray(value)) {
        // Check if array has any non-null items
        if (value.some((item) => item !== null && item !== undefined)) {
          // For arrays, recursively check if any item has values
          if (value.some((item) => (typeof item === 'object' ? hasValues(item) : item !== null))) {
            return true;
          }
        }
      } else if (typeof value === 'object') {
        // Recursively check nested objects
        if (hasValues(value)) return true;
      } else {
        // Primitive value that's not null
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const hasOldModels = linkedEntityModels && linkedEntityModels.length > 0;
    const hasNextGenModels = linkedNextGenModels.length > 0;

    if (!hasOldModels && !hasNextGenModels) return;

    // Start with message entities
    let newEntities = { ...message.entities };

    // Initialize old models with empty entities if not present
    if (hasOldModels) {
      for (const model of linkedEntityModels) {
        if (!newEntities[model.id]) {
          newEntities[model.id] = model.tags.reduce(
            (acc, tag) => {
              acc[tag.internalId] = null;
              return acc;
            },
            {} as Entities[string]
          );
        }
      }
    }

    // Initialize next-gen models with empty entities if not present
    if (hasNextGenModels) {
      for (const model of linkedNextGenModels) {
        if (!newEntities[model.id]) {
          // Initialize with empty object - the full model will be fetched in NextGenEntityModelPanel
          newEntities[model.id] = {};
        }
      }
    }

    setEntities(newEntities);

    const entityEntries = Object.entries(newEntities);

    if (entityEntries.length === 1) {
      // If there's only one entity, always open it (skip value checking)
      setAccordionOpen([entityEntries[0][0]]);
    } else if (entityEntries.length > 1) {
      // If there are multiple entities, open all that have values
      const entitiesWithValues = entityEntries
        .filter(([_, entityModel]) => hasValues(entityModel))
        .map(([modelId]) => modelId);

      setAccordionOpen(entitiesWithValues.length > 0 ? entitiesWithValues : [entityEntries[0][0]]);
    }
    // If no entities exist, don't open any accordion

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.entities, linkedNextGenModels]);

  const hasAnyLinkedModels = (linkedEntityModels?.length || 0) > 0 || linkedNextGenModels.length > 0;

  // Sort entities so next-gen extraction models appear first
  // This must be before any early returns to satisfy Rules of Hooks
  const sortedEntities = useMemo(() => {
    const entries = Object.entries(entities);
    return entries.sort(([modelIdA], [modelIdB]) => {
      // Check if models are next-gen (either in linkedNextGenModels or start with ext_mdl_)
      const isNextGenA = linkedNextGenModels.some((m) => m.id === modelIdA) || modelIdA.startsWith('ext_mdl_');
      const isNextGenB = linkedNextGenModels.some((m) => m.id === modelIdB) || modelIdB.startsWith('ext_mdl_');

      // Next-gen models come first
      if (isNextGenA && !isNextGenB) return -1;
      if (!isNextGenA && isNextGenB) return 1;

      // Within same category, sort alphabetically
      return modelIdA.localeCompare(modelIdB);
    });
  }, [entities, linkedNextGenModels]);

  if (!hasAnyLinkedModels) {
    return (
      <EmptyStateCard
        icon="ScanSearch"
        title="No extraction model"
        description="Create or link an extraction model to automatically pull key information from emails"
      />
    );
  }

  // this method is used for every entity edit
  function onEditSave(modelId: string, values: string[] | null, path: string[], action: ActualsUpdateType) {
    const oldValue = getEntityValue(entities[modelId], path);

    const newEntities = setEntityValue(entities[modelId], path, values);
    setEntities((prev) => ({ ...prev, [modelId]: newEntities }));

    const tagPathKey = path.join('.');
    const actuals = (message.actuals?.[modelId] || {}) as ActualsMetaNer;
    const updates = actuals?.updates?.[tagPathKey] ?? [];

    updates.push({
      action,
      oldValues: oldValue as any,
      newValues: values,
      user: {
        id: user?.sub,
        name: user?.name,
        email: user?.email,
      },
      timestamp: new Date().toISOString(),
    });

    updateMessage.mutate({
      integrationId: message.integration_id,
      threadId: message.thread_id,
      modelId,
      entities: newEntities,
      meta: {
        ...actuals,
        type: 'ENTITY',
        model_type: 'ner',
        updates: {
          ...actuals?.updates,
          [tagPathKey]: updates,
        },
      },
    });
    mixpanel.track(`Tag ${action}`);
    onEntityChange?.(action);
  }

  function handleMarkAllAsCorrect(modelId: string) {
    const actuals = message.actuals?.[modelId] as ActualsMetaNer;
    updateMessage.mutate({
      integrationId: message.integration_id,
      threadId: message.thread_id,
      modelId,
      entities: entities[modelId],
      meta: {
        type: 'ENTITY',
        model_type: 'ner',
        verified: true,
        updates: actuals?.updates || {},
      },
    });
    mixpanel.track('Mark All As Correct');
  }

  function handleAddArrayGroup(modelId: string, path: string[], parentTag: Tag) {
    if (!parentTag.children?.length) return;
    const model = modelMap.get(modelId);
    if (!model?.tags?.get(parentTag.internalId)) return;

    const fullPath = path.concat(parentTag.internalId);
    const currentValue = getEntityValue(entities[modelId], fullPath) || [];

    if (!Array.isArray(currentValue)) {
      throw new Error('Current value is not an array');
    }

    const children = parentTag.children?.map(({ id }) => ({
      ...model.tags.get(id),
    }));

    const newObject = children.reduce(
      (acc, child) => {
        if (!child.internalId) {
          return acc;
        }
        acc[child.internalId] = null;
        return acc;
      },
      {} as Entities[string]
    );

    const newEntities = setEntityValue(entities[modelId], fullPath, [...currentValue, newObject]);
    setEntities((prev) => ({ ...prev, [modelId]: newEntities }));
  }

  function handleAddMissingFields(modelId: string, path: string[], parentTag: Tag) {
    const parentPath = path.concat(parentTag.internalId);
    const currentValue = getEntityValue(entities[modelId], parentPath) || {};

    for (const { id } of parentTag.children || []) {
      const childTag = modelMap.get(modelId)?.tags.get(id);
      if (!childTag) continue;
      currentValue[childTag.internalId] = null;
    }

    setEntities((prev) => ({
      ...prev,
      [modelId]: setEntityValue(prev[modelId], parentPath, currentValue),
    }));
  }

  function handleDeleteArrayItem(modelId: string, path: string[]) {
    const index = parseInt(path[path.length - 1]);
    const parentArrayPath = path.slice(0, -1); // slice of the index
    const model = modelMap.get(modelId);
    const parentTag = model?.tags.get(parentArrayPath[parentArrayPath.length - 1]);

    if (!parentTag) {
      throw new Error('Parent tag not found');
    }

    const currentValue = getEntityValue(entities[modelId], parentArrayPath) || [];

    if (!Array.isArray(currentValue)) {
      throw new Error('Current value is not an array');
    }

    const newEntities = setEntityValue(
      entities[modelId],
      parentArrayPath,
      // we dont remove the item so our path accessor indexes remain correct
      currentValue.map((value, i) => (i === index ? null : value))
    );

    const actuals = message.actuals?.[modelId] as ActualsMetaNer;
    const updates = actuals?.updates || {};

    setEntities((prev) => ({
      ...prev,
      [modelId]: newEntities,
    }));

    // create updates for each child tag that is being removed
    for (const { id } of parentTag.children || []) {
      const childTag = model?.tags.get(id);
      if (!childTag) {
        continue;
      }
      const childPath = path.concat(childTag?.internalId);
      const childUpdates = updates[childPath.join('.')] || [];
      const oldValues = getEntityValue(entities[modelId], childPath);
      if (!oldValues) {
        continue;
      }
      childUpdates.push({
        action: 'REMOVE',
        oldValues: oldValues,
        newValues: null,
        timestamp: new Date().toISOString(),
        user: {
          id: user?.sub,
          name: user?.name,
          email: user?.email,
        },
      });
      updates[childPath.join('.')] = childUpdates;
    }

    updateMessage.mutate({
      integrationId: message.integration_id,
      threadId: message.thread_id,
      modelId,
      entities: newEntities,
      meta: {
        ...actuals,
        updates: {
          ...actuals.updates,
          ...updates,
        },
      },
    });
  }

  function handleDuplicateArrayItem(modelId: string, path: string[]) {
    const valueToDuplicate = getEntityValue(entities[modelId], path);
    const parentArrayPath = path.slice(0, -1);
    const parentArrayValue = getEntityValue(entities[modelId], parentArrayPath) || [];

    if (!Array.isArray(parentArrayValue)) {
      throw new Error('Parent array value is not an array');
    }

    setEntities((prev) => ({
      ...prev,
      [modelId]: setEntityValue(prev[modelId], parentArrayPath, [...parentArrayValue, valueToDuplicate]),
    }));
  }

  function handleCopyRaw(modelId: string) {
    navigator.clipboard.writeText(JSON.stringify(entities[modelId], null, 4));
    toast({
      title: 'Copied to clipboard',
      variant: 'success',
    });
  }

  return (
    <Accordion ref={accordionRef} type="multiple" value={accordionOpen} onValueChange={setAccordionOpen}>
      {sortedEntities.map(([modelId, modelEntities]) => {
        const isFullyVerified = message.actuals?.[modelId]?.verified;
        const shouldUseSmallLayout = smallVersion;

        // Check if this is a next-gen model
        const nextGenModel = linkedNextGenModels.find((m) => m.id === modelId);
        if (nextGenModel) {
          return (
            <NextGenEntityModelPanel
              key={modelId}
              modelId={modelId}
              modelListItem={nextGenModel}
              modelEntities={modelEntities}
              integrationId={message.integration_id}
              threadId={message.thread_id}
              isFullyVerified={!!isFullyVerified}
              isSaving={throttledLoading}
              onEntityChange={(key, value, path) => {
                // Update entity value for next-gen models using the full path
                // IMPORTANT: Do NOT transform values - save in the same format received

                // If path is empty, value is the full updated entities object (from skeleton merge)
                const newEntities =
                  path.length === 0
                    ? (value as Entities[string])
                    : setEntityValue(modelEntities, path, value as Entities[string][string]);

                const oldValue = path.length === 0 ? modelEntities : getEntityValue(modelEntities, path);

                // Check if value actually changed - only make request if different
                if (isDeepEqual(oldValue, value)) {
                  // Value hasn't changed, update local state but skip backend request
                  setEntities((prev) => ({ ...prev, [modelId]: newEntities }));
                  return;
                }

                setEntities((prev) => ({ ...prev, [modelId]: newEntities }));

                // Persist to backend
                const tagPathKey = path.join('.');
                const actuals = (message.actuals?.[modelId] || {}) as ActualsMetaNer;
                const updates = actuals?.updates?.[tagPathKey] ?? [];

                updates.push({
                  action: 'UPDATE',
                  oldValues: oldValue as unknown as string[] | null,
                  newValues: value as unknown as string[] | null,
                  user: {
                    id: user?.sub,
                    name: user?.name,
                    email: user?.email,
                  },
                  timestamp: new Date().toISOString(),
                });

                updateMessage.mutate({
                  integrationId: message.integration_id,
                  threadId: message.thread_id,
                  modelId,
                  entities: newEntities,
                  meta: {
                    ...actuals,
                    type: 'ENTITY',
                    model_type: 'ner',
                    updates: {
                      ...actuals?.updates,
                      [tagPathKey]: updates,
                    },
                  },
                });
                mixpanel.track('Tag UPDATE');
                onEntityChange?.('UPDATE');
              }}
              onPredictionComplete={(prediction) => {
                // Update local state with the prediction result
                const predictionEntities = prediction as Entities[string];
                setEntities((prev) => ({ ...prev, [modelId]: predictionEntities }));

                // Persist prediction to backend
                const actuals = (message.actuals?.[modelId] || {}) as ActualsMetaNer;
                updateMessage.mutate({
                  integrationId: message.integration_id,
                  threadId: message.thread_id,
                  modelId,
                  entities: predictionEntities,
                  meta: {
                    ...actuals,
                    type: 'ENTITY',
                    model_type: 'ner',
                    updates: actuals?.updates || {},
                  },
                });
              }}
              onMarkAllAsCorrect={() => handleMarkAllAsCorrect(modelId)}
            />
          );
        }

        // Old model rendering
        const model = modelMap.get(modelId);
        if (!model) {
          console.warn(`Model ${modelId} not found`);
          return null;
        }

        return (
          <AccordionItem key={modelId} value={modelId} className="border-none">
            <div className="flex items-center gap-1">
              <AccordionTrigger headless className="flex w-full items-center gap-1 group h-6">
                <ChevronRight
                  size={16}
                  className="text-typography-grey transition-transform duration-200 group-data-[state=open]:rotate-90"
                />
                <SubtleCapsHeaders>{model.displayName || modelId}</SubtleCapsHeaders>
                {isFullyVerified && (
                  <WithTooltip tooltip="This output has been fully verified">
                    <CheckCheck size={16} className="text-success" />
                  </WithTooltip>
                )}
                {throttledLoading && (
                  <div className="pl-2 flex items-center gap-1">
                    <Loading size={10} className="text-typography-grey" />
                    <span className="text-xs text-typography-">Saving...</span>
                  </div>
                )}
              </AccordionTrigger>

              <WithAccessControl>
                {!error.includes(modelId) && (
                  <div className="flex items-center ml-auto gap-2 pr-2">
                    <TripleDotActions
                      extra={[
                        {
                          text: 'Copy Raw',
                          icon: 'Copy',
                          onClick: () => {
                            handleCopyRaw(modelId);
                          },
                        },
                      ]}
                    >
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="text-typography-grey hover:text-typography-black transition-all h-4"
                      >
                        <MoreVertical size={16} />
                      </button>
                    </TripleDotActions>
                  </div>
                )}
              </WithAccessControl>
            </div>

            <AccordionContent className="pt-">
              {viewAs === 'RAW' ? (
                <div className="w-full border bg-white rounded-sm px-2">
                  <JsonViewer>{modelEntities}</JsonViewer>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <ErrorBoundary
                    fallbackRender={(props) => (
                      <EntityTreeErrorFallback
                        {...props}
                        reset={() => null}
                        entities={modelEntities}
                        onError={() => setError((prev) => [...prev, modelId])}
                      />
                    )}
                  >
                    {viewAs === 'TREE' && (
                      <SuperSmartEntityTree
                        actuals={message.actuals[modelId] as ActualsMetaNer}
                        entities={modelEntities}
                        tags={modelMap.get(modelId)?.tags || new Map()}
                        smallLayout={shouldUseSmallLayout}
                        onEditSave={(...params) => onEditSave(modelId, ...params)}
                        onDelete={(...params) => handleDeleteArrayItem(modelId, ...params)}
                        onAddArrayGroup={(...params) => handleAddArrayGroup(modelId, ...params)}
                        onDuplicate={(...params) => handleDuplicateArrayItem(modelId, ...params)}
                        onAddMissingFields={(...params) => handleAddMissingFields(modelId, ...params)}
                      />
                    )}
                    <button
                      onClick={() => handleMarkAllAsCorrect(modelId)}
                      disabled={throttledLoading || isFullyVerified}
                      className={cn(`text-sm select-none h-[30px] shrink-0 w-full transition-all text-center border text-success-600 
                        hover:bg-success-600 hover:text-white hover:border-success-600 bg-success-500/20 rounded-sm flex-center gap-2 px-2 py-1
                        border-success-600/20 disabled:pointer-events-none mt-1`)}
                    >
                      {throttledLoading ? <Loading size={14} color="#6B7280" /> : <CheckCheck size={16} />}
                      {isFullyVerified ? 'Fully Verified' : 'Mark all as correct'}
                    </button>
                  </ErrorBoundary>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
